// src/pages/patient/PatientNotifications.jsx
//
// /patient/notifications -- the full list behind the header bell
// (Patient Appointments Task 4). Paged from the server; same safe navigation.

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { readApiError } from "../../lib/apiErrors";
import { notificationRoute } from "../../lib/notificationLinks";
import useSequencedRequest from "../../lib/useSequencedRequest";
import { patientNotificationsApi } from "../../services/patientNotifications";
import { NotificationList } from "../../components/patient/notifications/NotificationBell";

const PAGE_SIZE = 20;

export default function PatientNotifications() {
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [actionError, setActionError] = useState("");
  const [markingAll, setMarkingAll] = useState(false);
  const list = useSequencedRequest(null);
  const run = list.run;

  const load = useCallback(
    () => run(({ signal }) => patientNotificationsApi.list({ limit: PAGE_SIZE, offset, unreadOnly }, { signal })),
    [run, offset, unreadOnly]
  );

  useEffect(() => {
    load();
  }, [load]);

  async function open(notification, route) {
    if (notification.is_unread) {
      try {
        await patientNotificationsApi.markRead(notification.id);
      } catch (err) {
        setActionError(readApiError(err, "Couldn't mark that notification as read."));
      }
    }
    if (route) navigate(route);
    else load();
  }

  async function markAll() {
    if (markingAll) return;
    setMarkingAll(true);
    setActionError("");
    try {
      await patientNotificationsApi.markAllRead();
      load();
    } catch (err) {
      setActionError(readApiError(err, "Couldn't mark notifications as read."));
    } finally {
      setMarkingAll(false);
    }
  }

  const data = list.data;
  const total = Number(data?.total || 0);
  return (
    <div className="mx-auto w-full max-w-3xl px-3 py-6 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Notifications</h1>
        <div className="flex gap-2">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={unreadOnly} onChange={(e) => { setOffset(0); setUnreadOnly(e.target.checked); }} />
            Unread only
          </label>
          <button type="button" onClick={markAll} disabled={markingAll || !Number(data?.unread_count || 0)} className="rounded-2xl border border-slate-300 px-3 py-1.5 text-sm font-semibold disabled:opacity-50">
            Mark all read
          </button>
        </div>
      </div>
      {actionError ? <p role="alert" className="mt-3 text-sm text-rose-700">{actionError}</p> : null}
      <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white">
        <NotificationList data={data} error={list.error} loading={list.loading} onOpen={(n) => open(n, notificationRoute(n))} onRetry={load} compact={false} />
      </div>
      {total > PAGE_SIZE ? (
        <nav className="mt-4 flex items-center justify-between" aria-label="Pagination">
          <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} className="rounded-2xl border border-slate-300 px-4 py-2 text-sm font-semibold disabled:opacity-50">Newer</button>
          <span className="text-sm text-slate-600">{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}</span>
          <button type="button" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)} className="rounded-2xl border border-slate-300 px-4 py-2 text-sm font-semibold disabled:opacity-50">Older</button>
        </nav>
      ) : null}
    </div>
  );
}
